import React, { useEffect, useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Dialog, DialogContent, DialogTitle, FormLabel, useMediaQuery } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import FormControl from '@material-ui/core/FormControl';
import Button from '@material-ui/core/Button';
import { HighlightOffRounded } from '@material-ui/icons';
import { CancelButton } from '../custom-components/CustomButtons';
import pluralize from 'pluralize';
import { useSelector } from 'react-redux';
import getProjectsState from '../../features/projects/selectors';
import ProjectAutocomplete from '../custom-components/ProjectAutocomplete';

interface FavoriteProjectsDialogProps {
  dialogOpen: boolean;
  handleClose: () => void;
  handleApply: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    projectIds: string[],
  ) => void;
  initialFavoriteProjectIds: string[];
}

const useStyles = makeStyles({
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    borderBottom: '1px solid #EDECEC',
  },
  title: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '26px',
    lineHeight: '30px',
    textAlign: 'left',
    color: '#0947B9',
  },
  rootIconButton: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  formControl: {
    width: '100%',
  },
  buttonContainer: {
    display: 'inline-flex',
    width: '100%',
    justifyContent: 'space-between',
    padding: '16px 12px 12px 0px',
    flexGrow: 1,
  },
  instructions: {
    paddingBottom: 16,
  },
  marginBottom: {
    marginBottom: 2,
  },
  button: {
    height: '32px',
    width: '120px',
  },
});

export default function FavoriteProjectsDialog(props: FavoriteProjectsDialogProps) {
  const classes = useStyles();
  const { dialogOpen, handleClose, handleApply, initialFavoriteProjectIds } = props;

  const projects = useSelector(getProjectsState);
  const isXs = useMediaQuery<Theme>((theme) => theme.breakpoints.down('xs'));

  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedProjectIds(initialFavoriteProjectIds);
  }, [initialFavoriteProjectIds]);

  return (
    <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm">
      <DialogTitle className={classes.titleContainer}>
        <Typography className={classes.title}>Favorite Projects</Typography>
        <IconButton
          style={{ right: '16px', top: '12px', position: 'absolute' }}
          onClick={handleClose}
          classes={{
            root: classes.rootIconButton,
          }}
        >
          <HighlightOffRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography className={classes.instructions}>
          {projects.length > 0 ? (
            <span>
              High-level information from the projects that you favorite will be displayed on the
              dashboard. You can change your favorite projects at any time.
            </span>
          ) : (
            <span>You don't have any projects.</span>
          )}
        </Typography>
        {projects.length > 0 && (
          <FormControl
            component="fieldset"
            variant="outlined"
            className={classes.formControl}
            focused={selectedProjectIds.length > 0}
          >
            <FormLabel component="legend" style={{ color: '#0947B9', marginBottom: 6 }}>
              {selectedProjectIds.length} favorite {pluralize('project', selectedProjectIds.length)}{' '}
              selected.
            </FormLabel>
            <ProjectAutocomplete
              selectedProjectIds={selectedProjectIds}
              setSelectedProjectIds={setSelectedProjectIds}
              style={{ width: isXs ? '100%' : '60%' }}
            />
          </FormControl>
        )}
        <div className={classes.buttonContainer}>
          <CancelButton onClick={handleClose} />
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={(event) => {
              handleApply(event, selectedProjectIds);
              handleClose();
            }}
          >
            APPLY
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
